import DownloadIcon from "@mui/icons-material/Download"
import {
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useState } from "react"
import { DateRangeFilter } from "../components/DateRangeFilter"
import {
  GroupFilterSelect,
  useGroupFilter,
} from "../components/GroupFilterSelect"
import { ReportPageHeader } from "../components/ReportPageHeader"
import { eventAttendanceReportPage } from "../constants/reportPages"
import {
  exportEventUserAttendanceSummaryAsCSV,
  GetEventUserAttendanceSummaryParams,
  getPaginationMeta,
  useGetEventUserAttendanceSummaryQuery,
} from "../services/api"
import { useAppSelector } from "../store/hooks"
import { selectActiveOrgId } from "../store/sessionSlice"

type EventUserAttendanceSummaryDateFilters = Pick<
  GetEventUserAttendanceSummaryParams,
  "start_date" | "end_date"
>

const columns: GridColDef[] = [
  {
    field: "eventTitle",
    headerName: "Event",
    width: 260,
    sortable: false,
    valueGetter: (_value, row) => row.event?.title || "Untitled Event",
  },
  {
    field: "start_date",
    headerName: "Start Date",
    width: 150,
    sortable: false,
    valueGetter: (_value, row) => {
      if (!row.event?.start_date) return "-"
      return new Date(row.event.start_date).toLocaleDateString()
    },
  },
  {
    field: "end_date",
    headerName: "End Date",
    width: 150,
    sortable: false,
    valueGetter: (_value, row) => {
      if (!row.event?.end_date) return "-"
      return new Date(row.event.end_date).toLocaleDateString()
    },
  },
  {
    field: "organization",
    headerName: "Organization",
    width: 220,
    sortable: false,
    valueGetter: (_value, row) => row.organization?.label || "-",
  },
  {
    field: "rsvp_count",
    headerName: "RSVP Count",
    width: 130,
    type: "number",
    sortable: false,
  },
  {
    field: "attended_count",
    headerName: "Attended Count",
    width: 150,
    type: "number",
    sortable: false,
  },
]

function EventUserAttendanceSummaryPage() {
  const activeOrgId = useAppSelector(selectActiveOrgId)
  const { selectedGroups, setSelectedGroups, groupParams } =
    useGroupFilter(activeOrgId)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [validationError, setValidationError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
  })
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<EventUserAttendanceSummaryDateFilters>
  >({})

  const queryParams = {
    ...appliedFilters,
    ...groupParams,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    org_id: activeOrgId,
  }

  const { data, error, isLoading } =
    useGetEventUserAttendanceSummaryQuery(queryParams)
  const paginationMeta = getPaginationMeta(data)

  const handleFilterChange = (
    field: "start_date" | "end_date",
    value: string
  ) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleApplyFilters = () => {
    const nextFilters: Partial<EventUserAttendanceSummaryDateFilters> = {}
    const startMs = filters.start_date ? Date.parse(filters.start_date) : null
    const endMs = filters.end_date ? Date.parse(filters.end_date) : null

    if (startMs !== null && Number.isNaN(startMs)) {
      setValidationError("Start date is invalid.")
      return
    }

    if (endMs !== null && Number.isNaN(endMs)) {
      setValidationError("End date is invalid.")
      return
    }

    if (startMs !== null && endMs !== null && startMs > endMs) {
      setValidationError("Start date must be before end date.")
      return
    }

    if (startMs !== null) {
      nextFilters.start_date = new Date(startMs).toISOString()
    }

    if (endMs !== null) {
      nextFilters.end_date = new Date(endMs).toISOString()
    }

    setValidationError(null)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    setAppliedFilters(nextFilters)
  }

  const handleClearFilters = () => {
    setSelectedGroups([])
    setValidationError(null)
    setPaginationModel({ page: 0, pageSize: 25 })
    setFilters({
      start_date: "",
      end_date: "",
    })
    setAppliedFilters({})
  }

  const handleExportCSV = () => {
    const exportParams = {
      ...appliedFilters,
      ...groupParams,
      org_id: activeOrgId,
    }

    exportEventUserAttendanceSummaryAsCSV(exportParams)
  }

  return (
    <Stack spacing={4}>
      <ReportPageHeader
        title={eventAttendanceReportPage.pageTitle}
        description={eventAttendanceReportPage.endpointDescription}
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={isLoading}
          >
            Export CSV
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            alignItems="flex-end"
          >
            <GroupFilterSelect
              organizationId={activeOrgId}
              value={selectedGroups}
              onChange={(value) => {
                setSelectedGroups(value)
                setPaginationModel((prev) => ({ ...prev, page: 0 }))
              }}
            />
            <DateRangeFilter
              label="Event Date Range"
              startDate={filters.start_date}
              endDate={filters.end_date}
              onStartDateChange={(value) =>
                handleFilterChange("start_date", value)
              }
              onEndDateChange={(value) => handleFilterChange("end_date", value)}
            />
            <Button variant="contained" onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {validationError && <Alert severity="warning">{validationError}</Alert>}

      {error && (
        <Alert severity="error">
          Error loading event attendance summary data. Please try again.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.data || []}
          getRowId={(row) => row.event.id}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={paginationMeta?.total || paginationMeta?.count || 0}
          paginationMode="server"
          loading={isLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Card>
    </Stack>
  )
}

export default EventUserAttendanceSummaryPage
